import {
  Badge,
  Button,
  Icon,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from "@nexus/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AnimationEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ScriptPlatformBadge } from "../../platform/ScriptPlatformBadge.js";
import { PERSONA_HUB_RESULTS_CLASS } from "../../../lib/persona-opportunity-stage-chrome.js";
import { CompetitorOpportunityCard } from "../../cards/PersonaOpportunityCard/CompetitorOpportunityCard.js";
import { PersonaOpportunityCard } from "../../cards/PersonaOpportunityCard/PersonaOpportunityCard.js";
import { FavoriteToggleButton } from "../../shared/FavoriteToggleButton/FavoriteToggleButton.js";
import { PersonaOpportunitySelectCheckbox } from "../../shared/PersonaOpportunitySelectCheckbox/PersonaOpportunitySelectCheckbox.js";
import {
  HUB_OPPORTUNITY_SELECTION_BAR_SCROLL_PADDING,
  HUB_SCRIPT_SELECTION_BAR_SCROLL_PADDING,
} from "./OpportunityHubActionBar.js";
import { OpportunityHubDocumentKindBadge } from "./OpportunityHubDocumentKindBadge.js";
import { OpportunityHubGroupFold } from "./OpportunityHubGroupFold.js";
import { OpportunityHubScriptGroupHeader } from "./OpportunityHubScriptGroupHeader.js";
import { OpportunityHubScriptGeneratedAt } from "./OpportunityHubScriptGeneratedAt.js";
import { OpportunityHubScriptMeta } from "./OpportunityHubScriptMeta.js";
import { OpportunityHubScriptRowSentBadge } from "./OpportunityHubScriptRowSentBadge.js";

import type { OpportunityHubGroup } from "./hub-chrome-grouping.js";
import { handleRowActivationKey } from "./hub-row-activation.js";
import { nextSort, type HubOpportunitySort } from "./hub-opportunity-table.js";
import { scriptListItems, type ScriptListItem } from "./hub-script-table.js";
import type {
  HubChromeOpportunityRow,
  HubChromeOrderRow,
  HubChromeScriptGroup,
  HubChromeScriptRow,
  HubSetsById,
} from "./hub-chrome.types.js";
import type { OpportunityHubViewMode } from "./hub-chrome.types.js";
import type { HubOpportunityWindow } from "./hub-opportunity-window.js";
import type { OpportunityHubListState } from "./OpportunityHubListFooter.js";
import { useHubVirtualList } from "./useHubVirtualList.js";
import { useOpportunityVirtualWindow } from "./useOpportunityVirtualWindow.js";
import type { HubOpportunityVirtualItem } from "./hub-opportunity-virtual-items.js";
import { hubOpportunitySelectionEnabled } from "./hub-fanout.js";

function groupBySet<T extends { setId: string; setName: string }>(
  rows: T[],
): { setName: string; rows: T[] }[] {
  const groups: { setName: string; rows: T[] }[] = [];
  const indexById = new Map<string, number>();
  for (const row of rows) {
    const existing = indexById.get(row.setId);
    if (existing == null) {
      indexById.set(row.setId, groups.length);
      groups.push({ setName: row.setName, rows: [row] });
    } else {
      groups[existing]!.rows.push(row);
    }
  }
  return groups;
}

export function OpportunityHubOpportunitiesCards({
  groups,
  selectedIds,
  onToggleSelect,
  onGenerateScripts,
  onView,
  footer,
  expandedGroupKeys,
  window: opportunityWindow,
  onToggleGroupExpanded,
  onLoadMoreGroups,
  onLoadMoreGroup,
  onGroupsNeeded,
  groupLoadGeneration = 0,
  showSetBadge = false,
  setsById,
}: {
  groups: OpportunityHubGroup[];
  selectedIds: ReadonlySet<string>;
  onToggleSelect: (opportunityId: string) => void;
  onGenerateScripts?: (row: HubChromeOpportunityRow) => void;
  onView?: (row: HubChromeOpportunityRow) => void;
  footer?: ReactNode;
  expandedGroupKeys?: ReadonlySet<string>;
  window?: HubOpportunityWindow;
  onToggleGroupExpanded?: (groupKey: string) => void;
  onLoadMoreGroups?: () => void;
  onLoadMoreGroup?: (groupKey: string) => void;
  onGroupsNeeded?: (groupKeys: readonly string[]) => void;
  groupLoadGeneration?: number;
  showSetBadge?: boolean;
  setsById?: HubSetsById;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const fallbackExpanded = useMemo(
    () => new Set(groups.map((group) => group.key)),
    [groups],
  );
  const expanded = expandedGroupKeys ?? fallbackExpanded;
  const cardsPerLine = useOpportunityCardsPerLine();
  const virtual = useOpportunityVirtualWindow({
    groups,
    expandedGroupKeys: expanded,
    loadingKeys: opportunityWindow?.loadingKeys ?? EMPTY_KEYS,
    hasMoreByKey: opportunityWindow?.hasMoreByKey ?? EMPTY_HAS_MORE,
    isLoadingMoreGroups: opportunityWindow?.isLoadingMoreGroups ?? false,
    hasMoreGroups: opportunityWindow?.hasMoreGroups ?? false,
    scrollRef,
    onLoadMoreGroups,
    onLoadMoreGroup,
    onGroupsNeeded,
    groupLoadGeneration,
    cardsPerLine,
    estimateItemSize: estimateCardItemSize,
  });

  const renderItem = (item: HubOpportunityVirtualItem) => {
    if (item.kind === "group") {
      return (
        <OpportunityHubGroupFold
          group={item.group}
          expanded={expanded.has(item.groupKey)}
          onToggle={() => onToggleGroupExpanded?.(item.groupKey)}
          showSetBadge={showSetBadge}
          setsById={setsById}
        />
      );
    }
    if (item.kind !== "cards") return <HubListSkeleton rows={1} />;
    return (
      <div className={cn("grid gap-4", cardsPerLine === 2 ? "grid-cols-2" : "grid-cols-1")}>
        {item.rows.map((row) => {
          const Card =
            row.setType === "COMPETITOR" ? CompetitorOpportunityCard : PersonaOpportunityCard;
          const openDrawer = onView
            ? () => onView(row)
            : undefined;
          return (
            <div
              key={row.id}
              className={cn(
                openDrawer &&
                  "cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              )}
              tabIndex={openDrawer ? 0 : undefined}
              onClick={(event) => {
                if (!openDrawer) return;
                const target = event.target;
                if (
                  target instanceof Element &&
                  target.closest("button, a, input, label, [role='checkbox']")
                ) {
                  return;
                }
                openDrawer();
              }}
              onKeyDown={
                openDrawer
                  ? (event) => handleRowActivationKey(event, openDrawer)
                  : undefined
              }
            >
              <Card
                row={row}
                selected={selectedIds.has(row.id)}
                selectionEnabled={hubOpportunitySelectionEnabled(row)}
                onToggleSelect={(id) => {
                  onToggleSelect(id);
                }}
                onGenerateScripts={
                  onGenerateScripts && !row.isCustom && row.setType !== "POST_FLIGHT"
                    ? () => onGenerateScripts(row)
                    : undefined
                }
              />
            </div>
          );
        })}
      </div>
    );
  };

  // Spacers resize as the window moves; browser scroll anchoring would fight
  // the virtualizer's own offset correction and make the scrollbar oscillate.
  return (
    <div
      ref={scrollRef}
      className={cn(
        "min-h-0 flex-1 overflow-auto [overflow-anchor:none]",
        selectedIds.size > 0 && HUB_OPPORTUNITY_SELECTION_BAR_SCROLL_PADDING,
      )}
    >
      <div aria-hidden style={{ height: virtual.topPad }} />
      {virtual.rendered.map((index) => {
        const item = virtual.items[index];
        if (!item) return null;
        return (
          <div key={item.key} data-index={index} ref={virtual.measureElement} className="pb-4">
            {renderItem(item)}
          </div>
        );
      })}
      <div aria-hidden style={{ height: virtual.bottomPad }} />
      {footer}
    </div>
  );
}

const EMPTY_KEYS: ReadonlySet<string> = new Set();
const EMPTY_HAS_MORE: Readonly<Record<string, boolean>> = {};

/** Mirrors Tailwind's `xl` breakpoint, where the card grid goes two-up. */
const CARDS_TWO_UP_QUERY = "(min-width: 1280px)";
const GROUP_FOLD_ESTIMATE_PX = 104;
const CARD_LINE_ESTIMATE_PX = 420;
const SKELETON_ESTIMATE_PX = 96;

function estimateCardItemSize(item: HubOpportunityVirtualItem): number {
  if (item.kind === "group") return GROUP_FOLD_ESTIMATE_PX;
  if (item.kind === "cards") return CARD_LINE_ESTIMATE_PX;
  return SKELETON_ESTIMATE_PX;
}

function useOpportunityCardsPerLine(): number {
  const [isTwoUp, setIsTwoUp] = useState(
    () => typeof window !== "undefined" && window.matchMedia?.(CARDS_TWO_UP_QUERY).matches === true,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia(CARDS_TWO_UP_QUERY);
    const update = () => setIsTwoUp(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return isTwoUp ? 2 : 1;
}

function HubListSkeleton({ rows = 4 }: { rows?: number }) {
  const { t } = useTranslation();
  return (
    <div role="status" className="flex flex-col gap-2 py-2">
      <span className="sr-only">{t("common.loading")}</span>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-16 w-full" />
      ))}
    </div>
  );
}

function ScriptFavoriteButton({
  row,
  onToggleFavorite,
}: {
  row: HubChromeScriptRow;
  onToggleFavorite: (row: HubChromeScriptRow) => void;
}) {
  return (
    <FavoriteToggleButton
      isFavorite={row.isFavorite}
      className="size-8"
      onToggle={(event) => {
        event.stopPropagation();
        onToggleFavorite(row);
      }}
    />
  );
}

const SCRIPT_TABLE_COLUMN_COUNT = 7;

function ScriptSortHead({
  columnId,
  label,
  sort,
  sortable,
  onSortChange,
  className,
}: {
  columnId: string;
  label: string;
  sort: HubOpportunitySort | null;
  sortable: boolean;
  onSortChange: (columnId: string) => void;
  className?: string;
}) {
  const sorted = sortable && sort?.columnId === columnId ? sort.direction : null;
  const sortIcon =
    sorted === "asc" ? "chevron-up" : sorted === "desc" ? "chevron-down" : "chevrons-up-down";

  return (
    <TableHead
      aria-sort={
        !sortable ? undefined : sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none"
      }
      className={cn("group/head select-none", sortable && "cursor-pointer", className)}
      onClick={sortable ? () => onSortChange(columnId) : undefined}
    >
      <span className="inline-flex items-center gap-1">
        <span className="truncate">{label}</span>
        {sortable ? (
          <Icon
            name={sortIcon}
            size="xs"
            aria-hidden
            className={cn(
              "shrink-0 transition-opacity",
              sorted
                ? "text-primary opacity-100"
                : "text-muted-foreground/40 opacity-0 group-hover/head:opacity-60",
            )}
          />
        ) : null}
      </span>
    </TableHead>
  );
}

const GROUP_REVEAL_CLASS =
  "animate-in fade-in slide-in-from-top-1 duration-150 ease-out motion-reduce:animate-none";

const SCRIPT_TABLE_ROW_ESTIMATE = 80;
const SCRIPT_CARD_LINE_ESTIMATE = 160;
const SCRIPT_CARDS_PER_LINE = 2;

export function OpportunityHubScriptsPanel({
  groups,
  expandedKeys,
  onToggleGroup,
  view,
  selectedIds,
  onToggleSelect,
  onToggleVisible,
  onView,
  onToggleFavorite,
  footer,
  listState,
  setsById,
  onPeekGroup,
}: {
  groups: HubChromeScriptGroup[];
  expandedKeys: ReadonlySet<string>;
  onToggleGroup: (groupKey: string) => void;
  onPeekGroup?: (groupKey: string) => void;
  view: OpportunityHubViewMode;
  selectedIds: ReadonlySet<string>;
  onToggleSelect: (scriptId: string) => void;
  onToggleVisible: () => void;
  onView: (row: HubChromeScriptRow) => void;
  onToggleFavorite: (row: HubChromeScriptRow) => void;
  footer?: ReactNode;
  listState?: OpportunityHubListState;
  setsById?: HubSetsById;
}) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [sort, setSort] = useState<HubOpportunitySort | null>(null);
  const hasMore = listState?.hasMore === true;
  if (hasMore && sort != null) setSort(null);
  const changeSort = (columnId: string) => {
    if (hasMore) return;
    setSort((current) => nextSort(current, columnId));
  };
  // Only the group just opened animates; clearing it on animation end keeps
  // virtualized rows from replaying the reveal when they remount on scroll.
  const [revealGroupKey, setRevealGroupKey] = useState<string | null>(null);
  const toggleGroup = (groupKey: string) => {
    setRevealGroupKey(expandedKeys.has(groupKey) ? null : groupKey);
    onToggleGroup(groupKey);
  };
  const revealClass = (groupKey: string) =>
    groupKey === revealGroupKey ? GROUP_REVEAL_CLASS : undefined;
  const endReveal = (event: AnimationEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) setRevealGroupKey(null);
  };
  const items = useMemo(
    () =>
      scriptListItems(
        groups,
        expandedKeys,
        sort,
        view === "table" ? 1 : SCRIPT_CARDS_PER_LINE,
      ),
    [groups, expandedKeys, sort, view],
  );
  const showSetName = useMemo(() => new Set(groups.map((group) => group.setId)).size > 1, [groups]);
  const groupOrdinals = useMemo(
    () => new Map(groups.map((group, index) => [group.key, index])),
    [groups],
  );
  const windowed = useHubVirtualList({
    count: items.length,
    estimateSize: view === "table" ? SCRIPT_TABLE_ROW_ESTIMATE : SCRIPT_CARD_LINE_ESTIMATE,
    scrollRef,
    hasMore: listState?.hasMore,
    isFetchingMore: listState?.isLoadingMore,
    onLoadMore: listState?.onLoadMore,
  });
  const visibleRows = items.flatMap((item) => (item.kind === "rows" ? item.rows : []));
  const allSelected =
    visibleRows.length > 0 && visibleRows.every((row) => selectedIds.has(row.id));
  const selectLabel = (title: string) =>
    t("personaOpportunities.hub.scripts.selectScriptAria", {
      title,
      defaultValue: `Select ${title}`,
    });
  const emptyGroupLabel = t(
    "personaOpportunities.hub.scripts.groupEmpty",
    "No scripts or briefs in this opportunity match these filters.",
  );
  const groupHeader = (item: Extract<ScriptListItem, { kind: "group" }>, striped = false) => (
    <OpportunityHubScriptGroupHeader
      group={item.group}
      expanded={item.expanded}
      showSetName={showSetName}
      striped={striped}
      set={setsById?.get(item.group.setId)}
      onToggle={() => toggleGroup(item.group.key)}
      onPeek={onPeekGroup ? () => onPeekGroup(item.group.key) : undefined}
    />
  );
  const isStripedGroup = (groupKey: string) => (groupOrdinals.get(groupKey) ?? 0) % 2 === 1;

  if (view === "table") {
    const fullWidthRow = (key: string, content: ReactNode, className?: string) => (
      <TableRow key={key} className={cn("hover:bg-transparent", className)}>
        <TableCell colSpan={SCRIPT_TABLE_COLUMN_COUNT} className="p-0">
          {content}
        </TableCell>
      </TableRow>
    );
    return (
      <div
        ref={scrollRef}
        className={cn(
          PERSONA_HUB_RESULTS_CLASS,
          selectedIds.size > 0 && HUB_SCRIPT_SELECTION_BAR_SCROLL_PADDING,
        )}
        data-opportunity-hub-scripts=""
      >
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <PersonaOpportunitySelectCheckbox
                  checked={allSelected}
                  onToggle={onToggleVisible}
                  label={t(
                    "personaOpportunities.hub.scripts.selectAllAria",
                    "Select all visible scripts and briefs",
                  )}
                />
              </TableHead>
              <ScriptSortHead
                columnId="kind"
                label={t("personaOpportunities.hub.scripts.columns.kind", "Kind")}
                sort={sort}
                sortable={!hasMore}
                onSortChange={changeSort}
                className="w-[84px] pr-1"
              />
              <TableHead className="w-8 px-0">
                <span className="sr-only">
                  {t("personaOpportunities.hub.scripts.columns.favorite", "Favorite")}
                </span>
              </TableHead>
              <ScriptSortHead
                columnId="title"
                label={t("personaOpportunities.hub.scripts.columns.title", "Title")}
                sort={sort}
                sortable={!hasMore}
                onSortChange={changeSort}
              />
              <TableHead className="w-36">
                {t("personaOpportunities.hub.scripts.columns.set", "Set")}
              </TableHead>
              <ScriptSortHead
                columnId="generatedAt"
                label={t("personaOpportunities.hub.scripts.columns.generatedAt", "Generated at")}
                sort={sort}
                sortable={!hasMore}
                onSortChange={changeSort}
                className="w-36"
              />
              <ScriptSortHead
                columnId="sent"
                label={t("personaOpportunities.hub.scripts.columns.sent", "Send status")}
                sort={sort}
                sortable={!hasMore}
                onSortChange={changeSort}
                className="w-40"
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {windowed.topPad > 0 ? (
              <TableRow aria-hidden>
                <TableCell colSpan={SCRIPT_TABLE_COLUMN_COUNT} style={{ height: windowed.topPad, padding: 0, border: 0 }} />
              </TableRow>
            ) : null}
            {windowed.indexes.map((index) => {
              const item = items[index];
              if (!item) {
                return fullWidthRow("scripts-more", <HubListSkeleton rows={1} />);
              }
              if (item.kind === "group") {
                return fullWidthRow(
                  item.key,
                  groupHeader(item, isStripedGroup(item.group.key)),
                  "border-border",
                );
              }
              if (item.kind === "loading") {
                return fullWidthRow(
                  item.key,
                  <HubListSkeleton rows={1} />,
                  cn("px-4", revealClass(item.groupKey)),
                );
              }
              if (item.kind === "empty") {
                return fullWidthRow(
                  item.key,
                  <p className="px-4 py-3 text-xs text-muted-foreground">{emptyGroupLabel}</p>,
                  revealClass(item.groupKey),
                );
              }
              return item.rows.map((row) => (
                <ScriptTableRow
                  key={row.id}
                  row={row}
                  selected={selectedIds.has(row.id)}
                  selectLabel={selectLabel(row.title)}
                  onToggleSelect={onToggleSelect}
                  onView={onView}
                  onToggleFavorite={onToggleFavorite}
                  className={revealClass(item.groupKey)}
                  onAnimationEnd={endReveal}
                />
              ));
            })}
            {windowed.bottomPad > 0 ? (
              <TableRow aria-hidden>
                <TableCell colSpan={SCRIPT_TABLE_COLUMN_COUNT} style={{ height: windowed.bottomPad, padding: 0, border: 0 }} />
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        {footer}
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className={cn(
        "min-h-0 flex-1 space-y-3 overflow-auto",
        selectedIds.size > 0 && HUB_SCRIPT_SELECTION_BAR_SCROLL_PADDING,
      )}
      data-opportunity-hub-scripts=""
    >
      {windowed.topPad > 0 ? <div aria-hidden style={{ height: windowed.topPad }} /> : null}
      {windowed.indexes.map((index) => {
        const item = items[index];
        if (!item) return <HubListSkeleton key="scripts-more" rows={1} />;
        if (item.kind === "group") {
          return (
            <div key={item.key} className="overflow-hidden rounded-md border border-border">
              {groupHeader(item)}
            </div>
          );
        }
        if (item.kind === "loading") return <HubListSkeleton key={item.key} rows={1} />;
        if (item.kind === "empty") {
          return (
            <p
              key={item.key}
              className={cn("px-1 text-xs text-muted-foreground", revealClass(item.groupKey))}
            >
              {emptyGroupLabel}
            </p>
          );
        }
        return (
          <div
            key={item.key}
            className={cn("grid grid-cols-1 gap-3 md:grid-cols-2", revealClass(item.groupKey))}
            onAnimationEnd={endReveal}
          >
            {item.rows.map((row) => (
              <ScriptCard
                key={row.id}
                row={row}
                selected={selectedIds.has(row.id)}
                selectLabel={selectLabel(row.title)}
                onToggleSelect={onToggleSelect}
                onView={onView}
                onToggleFavorite={onToggleFavorite}
              />
            ))}
          </div>
        );
      })}
      {windowed.bottomPad > 0 ? <div aria-hidden style={{ height: windowed.bottomPad }} /> : null}
      {footer}
    </div>
  );
}

interface ScriptItemProps {
  row: HubChromeScriptRow;
  selected: boolean;
  selectLabel: string;
  onToggleSelect: (scriptId: string) => void;
  onView: (row: HubChromeScriptRow) => void;
  onToggleFavorite: (row: HubChromeScriptRow) => void;
}

function ScriptTableRow({
  row,
  selected,
  selectLabel,
  onToggleSelect,
  onView,
  onToggleFavorite,
  className,
  onAnimationEnd,
}: ScriptItemProps & {
  className?: string;
  onAnimationEnd?: (event: AnimationEvent<HTMLElement>) => void;
}) {
  return (
    <TableRow
      data-state={selected ? "selected" : undefined}
      className={cn(
        "group/script cursor-pointer focus-visible:bg-muted/50 focus-visible:outline-none",
        className,
      )}
      onAnimationEnd={onAnimationEnd}
      tabIndex={0}
      onClick={() => onView(row)}
      onKeyDown={(event) => handleRowActivationKey(event, () => onView(row))}
    >
      <TableCell>
        <PersonaOpportunitySelectCheckbox
          checked={selected}
          onToggle={() => onToggleSelect(row.id)}
          label={selectLabel}
        />
      </TableCell>
      <TableCell className="pr-1">
        <OpportunityHubDocumentKindBadge row={row} />
      </TableCell>
      <TableCell className="px-0">
        <ScriptFavoriteButton row={row} onToggleFavorite={onToggleFavorite} />
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1.5">
          <p className="font-medium">{row.title}</p>
          <ScriptPlatformBadge format={row.youtubeFormat} />
        </div>
        <OpportunityHubScriptMeta row={row} showCreatedAt={false} className="mt-0.5" />
      </TableCell>
      <TableCell className="truncate" title={row.setName}>
        {row.setName}
      </TableCell>
      <TableCell>
        <OpportunityHubScriptGeneratedAt createdAt={row.createdAt} />
      </TableCell>
      <TableCell>
        <OpportunityHubScriptRowSentBadge row={row} />
      </TableCell>
    </TableRow>
  );
}

function ScriptCard({
  row,
  selected,
  selectLabel,
  onToggleSelect,
  onView,
  onToggleFavorite,
}: ScriptItemProps) {
  return (
    <article
      className={cn(
        "group/script cursor-pointer rounded-md border border-border bg-card p-4 transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none",
        selected && "border-primary ring-1 ring-primary",
      )}
      tabIndex={0}
      onClick={() => onView(row)}
      onKeyDown={(event) => handleRowActivationKey(event, () => onView(row))}
    >
      <div className="flex items-start gap-3">
        <PersonaOpportunitySelectCheckbox
          checked={selected}
          onToggle={() => onToggleSelect(row.id)}
          label={selectLabel}
          className="mt-0.5 shrink-0 self-start justify-start"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 items-start gap-2">
              <OpportunityHubDocumentKindBadge row={row} className="mt-px shrink-0" />
              <p className="text-[13px] font-medium text-foreground">{row.title}</p>
            </div>
            <ScriptFavoriteButton row={row} onToggleFavorite={onToggleFavorite} />
          </div>
          <OpportunityHubScriptMeta row={row} className="mt-1" />
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <ScriptPlatformBadge format={row.youtubeFormat} />
            <OpportunityHubScriptRowSentBadge row={row} />
          </div>
        </div>
      </div>
    </article>
  );
}

function orderStatusVariant(status: HubChromeOrderRow["status"]) {
  if (status === "delivered") return "status-delivered" as const;
  if (status === "in_production") return "status-in-production" as const;
  return "status-sent" as const;
}

function orderStatusLabel(
  t: ReturnType<typeof useTranslation>["t"],
  status: HubChromeOrderRow["status"],
) {
  if (status === "delivered") {
    return t("personaOpportunities.hub.sent.statusDelivered", "Delivered");
  }
  if (status === "in_production") {
    return t("personaOpportunities.hub.sent.statusInProduction", "In production");
  }
  return t("personaOpportunities.hub.sent.statusSent", "Sent");
}

export function OpportunityHubSentPanel({
  rows,
  view,
  footer,
  listState,
}: {
  rows: HubChromeOrderRow[];
  view: OpportunityHubViewMode;
  footer?: ReactNode;
  listState?: OpportunityHubListState;
}) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const groups = groupBySet(rows);
  const cardRows = groups.flatMap((group) => group.rows);
  const windowed = useHubVirtualList({
    count: view === "table" ? rows.length : Math.ceil(cardRows.length / 2),
    estimateSize: view === "table" ? 48 : 120,
    scrollRef,
    hasMore: listState?.hasMore,
    isFetchingMore: listState?.isLoadingMore,
    onLoadMore: listState?.onLoadMore,
  });

  if (view === "table") {
    return (
      <div ref={scrollRef} className={PERSONA_HUB_RESULTS_CLASS}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("personaOpportunities.hub.sent.columns.order", "Order")}</TableHead>
              <TableHead>{t("personaOpportunities.hub.sent.columns.set", "Set")}</TableHead>
              <TableHead>{t("personaOpportunities.hub.sent.columns.vendor", "Vendor")}</TableHead>
              <TableHead>{t("personaOpportunities.hub.sent.columns.status", "Status")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {windowed.topPad > 0 ? (
              <TableRow aria-hidden>
                <TableCell colSpan={4} style={{ height: windowed.topPad, padding: 0, border: 0 }} />
              </TableRow>
            ) : null}
            {windowed.indexes.map((index) => {
              const row = rows[index];
              if (!row) {
                return (
                  <TableRow key="orders-more" role="status">
                    <TableCell colSpan={4}>
                      <HubListSkeleton rows={1} />
                    </TableCell>
                  </TableRow>
                );
              }
              return (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{row.title}</TableCell>
                <TableCell>{row.setName}</TableCell>
                <TableCell>{row.vendor}</TableCell>
                <TableCell>
                  <Badge variant={orderStatusVariant(row.status)} dot>
                    {orderStatusLabel(t, row.status)}
                  </Badge>
                </TableCell>
              </TableRow>
              );
            })}
            {windowed.bottomPad > 0 ? (
              <TableRow aria-hidden>
                <TableCell colSpan={4} style={{ height: windowed.bottomPad, padding: 0, border: 0 }} />
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        {footer}
      </div>
    );
  }

  return (
    <div ref={scrollRef} className={`${PERSONA_HUB_RESULTS_CLASS} flex flex-col gap-3 px-4 py-4`}>
      <div style={{ height: windowed.topPad }} />
      {windowed.indexes.map((index) => {
        const pair = cardRows.slice(index * 2, index * 2 + 2);
        if (pair.length === 0) return <HubListSkeleton key="orders-more" rows={1} />;
        return (
          <div key={pair.map((row) => row.id).join(":")} className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {pair.map((row) => (
            <article key={row.id} className="rounded-md border border-border bg-card px-4 py-3">
              <p className="text-[13px] font-medium text-foreground">{row.title}</p>
              <p className="mt-1 text-[12px] text-muted-foreground">
                {row.setName} · {row.vendor}
              </p>
              <div className="mt-2">
                <Badge variant={orderStatusVariant(row.status)} dot>
                  {orderStatusLabel(t, row.status)}
                </Badge>
              </div>
            </article>
            ))}
          </div>
        );
      })}
      <div style={{ height: windowed.bottomPad }} />
      {footer}
    </div>
  );
}
